import { useTranslate } from "@similarweb/i18n-provider";
import { AddCircle, CollapseDiagonal, ExpandDiagonal } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Tooltip } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { ClickOutsideWrapper } from "app/custom-hooks/useClickOutside";

import { OnboardingAgentAssistantMessage } from "../OnboardingAgentAssistantMessage/OnboardingAgentAssistantMessage";
import { OnboardingAgentChatError } from "../OnboardingAgentChatError/OnboardingAgentChatError";
import { OnboardingAgentComposer } from "../OnboardingAgentComposer/OnboardingAgentComposer";
import { OnboardingAgentGreeting } from "../OnboardingAgentGreeting/OnboardingAgentGreeting";
import { OnboardingAgentLoadingIndicator } from "../OnboardingAgentLoadingIndicator/OnboardingAgentLoadingIndicator";
import { OnboardingAgentUserMessage } from "../OnboardingAgentUserMessage/OnboardingAgentUserMessage";
import {
    ONBOARDING_AGENT_PANEL_CLOSE_ARIA,
    ONBOARDING_AGENT_PANEL_CLOSE_TOOLTIP,
    ONBOARDING_AGENT_PANEL_COLLAPSE_HEIGHT_ARIA,
    ONBOARDING_AGENT_PANEL_EXPAND_HEIGHT_ARIA,
    ONBOARDING_AGENT_PANEL_EXPAND_TOOLTIP,
    ONBOARDING_AGENT_PANEL_MINIMIZE_TOOLTIP,
    ONBOARDING_AGENT_PANEL_NEW_CHAT_ARIA,
    ONBOARDING_AGENT_PANEL_NEW_CHAT_TOOLTIP,
    ONBOARDING_AGENT_PANEL_TITLE,
} from "../texts";

import { OnboardingAgentHeaderAiSparkles } from "./OnboardingAgentHeaderAiSparkles";
import type { UseOnboardingAgentPanelResult } from "./OnboardingAgentPanel.types";
import {
    ChatBody,
    ChatBodyMain,
    ChatBodyScrollSpacer,
    ChatHeader,
    HeaderActionButton,
    HeaderActions,
    HeaderLead,
    HeaderTitle,
    PanelRoot,
} from "./styles";
import { useOnboardingAgentFocusTrap } from "./hooks/useOnboardingAgentFocusTrap";

type OnboardingAgentPanelProps = {
    chat: UseOnboardingAgentPanelResult;
    isFirstOpen: boolean;
    isExpanded: boolean;
    onToggleExpand: () => void;
};

export const OnboardingAgentPanel = ({ chat, isFirstOpen, isExpanded, onToggleExpand }: OnboardingAgentPanelProps) => {
    const { translate } = useTranslate();
    const {
        messages,
        replyError,
        activeRequest,
        composerFocusTrigger,
        streamingMessageId,
        scrollContainerRef,
        scrollContentRef,
        scrollSpacerRef,
        lastUserMessageRef,
        handleCloseButtonClick,
        handleClickOutside,
        handleSend,
        handleExploreNextLanding,
        handleStop,
        handleNewChat,
        handleStreamComplete,
    } = chat;
    const lastUserMessageIndex = messages.findLastIndex((message) => message.role === "user");
    const { focusTrapRef, handleFocusTrapKeyDown } = useOnboardingAgentFocusTrap();

    const expandToggleAriaLabel = translate(
        isExpanded ? ONBOARDING_AGENT_PANEL_COLLAPSE_HEIGHT_ARIA : ONBOARDING_AGENT_PANEL_EXPAND_HEIGHT_ARIA,
    );
    const ExpandToggleIcon = isExpanded ? CollapseDiagonal : ExpandDiagonal;

    return (
        <ClickOutsideWrapper onClickOutside={handleClickOutside}>
            <PanelRoot
                ref={focusTrapRef}
                flexDirection="column"
                role="dialog"
                aria-modal="true"
                aria-label={translate(ONBOARDING_AGENT_PANEL_TITLE)}
                onKeyDown={handleFocusTrapKeyDown}
                $isExpanded={isExpanded}
            >
                <ChatHeader data-automation="onboarding-agent-panel-header">
                    <HeaderLead>
                        <OnboardingAgentHeaderAiSparkles />
                        <HeaderTitle
                            fontVariant="listItem1"
                            color={colorsPalettes.carbon[500]}
                            data-automation="onboarding-agent-panel-title"
                        >
                            {translate(ONBOARDING_AGENT_PANEL_TITLE)}
                        </HeaderTitle>
                    </HeaderLead>
                    <HeaderActions>
                        <Tooltip
                            content={translate(ONBOARDING_AGENT_PANEL_NEW_CHAT_TOOLTIP)}
                            placement="top"
                        >
                            <HeaderActionButton
                                type="flat"
                                width={32}
                                height={32}
                                label={
                                    <AddCircle
                                        size={16}
                                        color={colorsPalettes.carbon[300]}
                                    />
                                }
                                onClick={handleNewChat}
                                aria-label={translate(ONBOARDING_AGENT_PANEL_NEW_CHAT_ARIA)}
                                dataAutomation="onboarding-agent-panel-new-chat"
                            />
                        </Tooltip>
                        <Tooltip
                            content={translate(
                                isExpanded
                                    ? ONBOARDING_AGENT_PANEL_MINIMIZE_TOOLTIP
                                    : ONBOARDING_AGENT_PANEL_EXPAND_TOOLTIP,
                            )}
                            placement="top"
                        >
                            <HeaderActionButton
                                type="flat"
                                width={32}
                                height={32}
                                label={
                                    <ExpandToggleIcon
                                        size={16}
                                        color={colorsPalettes.carbon[300]}
                                    />
                                }
                                onClick={onToggleExpand}
                                aria-label={expandToggleAriaLabel}
                                aria-pressed={isExpanded}
                                dataAutomation="onboarding-agent-panel-toggle-expand"
                            />
                        </Tooltip>
                        <Tooltip
                            content={translate(ONBOARDING_AGENT_PANEL_CLOSE_TOOLTIP)}
                            placement="top"
                        >
                            <IconButton
                                type="flat"
                                iconName="clear"
                                iconSize="xs"
                                onClick={handleCloseButtonClick}
                                aria-label={translate(ONBOARDING_AGENT_PANEL_CLOSE_ARIA)}
                                data-automation="onboarding-agent-panel-close"
                            />
                        </Tooltip>
                    </HeaderActions>
                </ChatHeader>
                <ChatBody data-automation="onboarding-agent-panel-body">
                    <ChatBodyMain ref={scrollContainerRef}>
                        <Stack
                            ref={scrollContentRef}
                            gap="16px"
                            width="100%"
                        >
                            {messages.length === 0 ? <OnboardingAgentGreeting isFirstOpen={isFirstOpen} /> : null}
                            {messages.map((message, index) =>
                                message.role === "user" ? (
                                    <OnboardingAgentUserMessage
                                        key={message.id}
                                        text={message.text}
                                        ref={index === lastUserMessageIndex ? lastUserMessageRef : undefined}
                                    />
                                ) : (
                                    <OnboardingAgentAssistantMessage
                                        key={message.id}
                                        text={message.text}
                                        messageIndex={index}
                                        copyPlainText={message.copyPlainText}
                                        threadId={message.threadId}
                                        exploreNextItems={message.exploreNextItems}
                                        followUpQuestions={message.followUpQuestions}
                                        quickActions={message.quickActions}
                                        suggestedActions={message.suggestedActions}
                                        isFollowUpDisabled={activeRequest !== null}
                                        shouldStream={message.id === streamingMessageId}
                                        onStreamComplete={() => handleStreamComplete(message.id)}
                                        onAskFollowUp={handleSend}
                                        onInAppQuickLinkLanding={handleExploreNextLanding}
                                    />
                                ),
                            )}
                            {replyError ? <OnboardingAgentChatError onRetry={replyError.onRetry} /> : null}
                            {activeRequest ? <OnboardingAgentLoadingIndicator /> : null}
                        </Stack>
                        <ChatBodyScrollSpacer
                            ref={scrollSpacerRef}
                            aria-hidden="true"
                            data-automation="onboarding-agent-panel-scroll-spacer"
                        />
                    </ChatBodyMain>
                    <OnboardingAgentComposer
                        focusTrigger={composerFocusTrigger}
                        isLoading={activeRequest !== null}
                        onSend={handleSend}
                        onStop={handleStop}
                    />
                </ChatBody>
            </PanelRoot>
        </ClickOutsideWrapper>
    );
};
